import Warning from "@mui/icons-material/Warning";
import { Stack, Tooltip, Typography } from "@mui/material";
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF, getGridDateOperators } from "@mui/x-data-grid-pro";
import { useMemo } from "react";
import { isEmailChannel } from "../../../../modules/channels/types";
import { AnalyticsDatetime } from "../../utils/analytics-datetime";
import { CustomDetailPanelToggle } from "../send-table/custom-detial-panel-toggle";
import { TableStack } from "../send-table/table-stack";
import { ValueCellDisplay } from "../send-table/value-cell-display";

import type { ChannelName } from "../../../../modules/channels/types";
import type { ISO8601 } from "../../../../utils/iso8601";
import type { AnalyticsMoment } from "../types";
import type { TransformedGroupedMomentAnalytics } from "./utils";
import type { GridColDef, GridRenderCellParams, GridValidRowModel } from "@mui/x-data-grid-pro";

export function useSendTableColumns<T extends TransformedGroupedMomentAnalytics>(
  moment?: AnalyticsMoment,
): GridColDef<T>[] {
  return useMemo(
    () => [
      {
        ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
        renderCell: (params) => <CustomDetailPanelToggle {...params} />,
        hideable: false,
      },
      {
        field: "min_timestamp",
        type: "dateTime",
        headerName: "Sent",
        filterOperators: getGridDateOperators(true).filter(
          (operator) => operator.value === "before" || operator.value === "after",
        ),
        flex: 1.5,
        renderCell: (params: GridRenderCellParams<T, ISO8601>) =>
          params.value ? (
            <TableStack tooltip="Date and time the message was sent">
              <AnalyticsDatetime datetime={params.value} />
            </TableStack>
          ) : null,
      },
      {
        field: "recipients",
        headerName: "Recipients",
        sortable: false,
        filterable: false,
        flex: 1,
        renderCell: (params: GridRenderCellParams<T, T["recipients"]>) => (
          <TableStack>
            <Tooltip
              title={(() => {
                // TODO this is the moment's current segment not the segment which was
                // this actual send.
                const blurb = moment?.segment?.name
                  ? `Number of recipients ${moment?.target_connection_type ? "connected to subjects in" : "in"} segment` +
                    ` "${moment?.segment.name}"`
                  : "Number of recipients";

                switch (params.value?.type) {
                  case "channel_name":
                    return "Channel the message was sent to";
                  case "person_name":
                    // This is only ever singular at the moment
                    return "Person the message was sent to";
                  case "number":
                    return blurb;
                  case undefined:
                  case null:
                  default:
                    return undefined;
                }
              })()}
              arrow
              placement="top"
            >
              <Typography variant="body2">{params.value ? params.value.display : "-"}</Typography>
            </Tooltip>

            {params.value?.connection_type && (
              <Tooltip title="Recipients’ connection to subjects" placement="left" arrow>
                <Typography variant="body2" color="textSecondary" textTransform="capitalize">
                  {params.value.connection_type}
                </Typography>
              </Tooltip>
            )}
            <Stack direction="row" spacing={1} alignItems="center">
              {Boolean(params.value?.cc?.length) && params.value!.cc!.length > 0 && (
                <Tooltip title={params.value!.cc!.join(", ")} placement="bottom" arrow>
                  <Typography variant="body2" color="textSecondary">
                    +CC
                  </Typography>
                </Tooltip>
              )}
              {Boolean(params.value?.bcc?.length) && params.value!.bcc!.length > 0 && (
                <Tooltip title={params.value!.bcc!.join(", ")} placement="bottom" arrow>
                  <Typography variant="body2" color="textSecondary">
                    +BCC
                  </Typography>
                </Tooltip>
              )}
            </Stack>
          </TableStack>
        ),
      },
      {
        field: "subject",
        headerName: "Subject/Trigger",
        sortable: false,
        filterable: false,
        flex: 1,
        renderCell: (params: GridRenderCellParams<T, T["subject"]>) => (
          <TableStack
            tooltip={(() => {
              if (params.value?.subject && params.value?.when) {
                return "Scheduling and subject of the message";
              }

              if (params.value?.subject) {
                return "Subject of the message";
              }
              if (params.value?.when) {
                return "Scheduling of the message";
              }
              return undefined;
            })()}
          >
            <Typography variant="body2">{params.value ? params.value.when : "-"}</Typography>
            {params.value?.subject && (
              <Typography variant="body2" color="textSecondary">
                {params.value.subject}
              </Typography>
            )}
          </TableStack>
        ),
      },
      {
        field: "delivered",
        headerName: "Delivered",
        flex: 0.5,
        sortable: false,
        filterable: false,
        align: "right",
        renderCell: ({ value }: GridRenderCellParams<GridValidRowModel, T["delivered"]>) => (
          <TableStack tooltip="Percentage of messages that were delivered successfully">
            <Stack direction="row" spacing={1} alignItems="flex-start" height="100%">
              <Typography variant="body2">
                <ValueCellDisplay cellData={value} />
              </Typography>
              {value?.type === "percentage" && value.value < 100 && (
                <Tooltip title="Some messages could not be delivered" arrow placement="bottom">
                  <Warning color="error" fontSize="small" />
                </Tooltip>
              )}
            </Stack>
          </TableStack>
        ),
      },
      {
        field: "opened",
        headerName: "Opened",
        flex: 0.5,
        sortable: false,
        filterable: false,
        align: "right",
        renderCell: (params: GridRenderCellParams<GridValidRowModel, T["opened"]>) => (
          <TableStack
            tooltip={
              // oxlint-disable-next-line typescript/no-unsafe-type-assertion
              isEmailChannel(params.row.channel_name as ChannelName)
                ? "Percentage of messages that were opened"
                : undefined
            }
          >
            <Stack direction="row" spacing={1} alignItems="flex-start" height="100%">
              <Typography variant="body2">
                {
                  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
                  isEmailChannel(params.row.channel_name as ChannelName) ? (
                    <ValueCellDisplay cellData={params.value} />
                  ) : (
                    "-"
                  )
                }
              </Typography>
            </Stack>
          </TableStack>
        ),
      },
      {
        field: "interacted",
        headerName: "Interacted",
        flex: 0.5,
        sortable: false,
        filterable: false,
        align: "right",
        renderCell: (params: GridRenderCellParams<GridValidRowModel, T["interacted"]>) => (
          <TableStack
            tooltip={(() => {
              switch (params.value?.type) {
                case "percentage":
                  return "Percentage of messages where recipients clicked on a link/button";
                case "number":
                  return "Number of messages where recipients clicked on a link/button";
                case undefined:
                case null:
                default:
                  return undefined;
              }
            })()}
          >
            <Stack direction="row" spacing={1} alignItems="flex-start" height="100%">
              <Typography variant="body2">
                <ValueCellDisplay cellData={params.value} />
              </Typography>
            </Stack>
          </TableStack>
        ),
      },
    ],
    [moment],
  );
}
